import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, ImageUp, Disc3, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, STREAM_URL, useStudioApi, errMsg } from "@/lib/api";
import { copy } from "@/components/listen/StreamLinks";

const SITE = window.location.origin;
const EMBED = `<audio controls preload="none" src="${STREAM_URL}"></audio>`;

const CopyRow = ({ label, value, testid }) => (
  <div className="flex flex-col gap-1.5">
    <Label>{label}</Label>
    <div className="flex gap-2">
      <code className="flex-1 min-w-0 truncate rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 font-mono text-xs">{value}</code>
      <button onClick={() => copy(value, `${label} copied`)} className="rounded-lg bg-[color:var(--gold)] text-black px-3 font-bold flex items-center gap-1.5 text-sm" data-testid={testid}><Copy className="w-4 h-4" /> Copy</button>
    </div>
  </div>
);

export const SettingsPanel = () => {
  const call = useStudioApi();
  const [form, setForm] = useState({ name: "", tagline: "" });
  const [logo, setLogo] = useState({ has_logo: false, logo_version: 0, has_background: false, background_version: 0 });
  const [saving, setSaving] = useState(false);
  const file = useRef(null);
  const bgFile = useRef(null);

  useEffect(() => {
    axios.get(`${API}/station/status`).then(({ data }) => {
      setForm({ name: data.station.name, tagline: data.station.tagline });
      setLogo(data.station);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try { await call("put", "/studio/settings", form); toast.success("Station settings saved"); } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const uploadImage = async (kind, f) => {
    if (!f) return;
    const data = new FormData();
    data.append("file", f);
    try { setLogo(await call("post", `/studio/image/${kind}`, data)); toast.success(kind === "logo" ? "Logo updated" : "Background updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const removeBackground = async () => {
    try { setLogo(await call("delete", "/studio/image/background")); toast.success("Background removed"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="panel p-5 mt-3 grid grid-cols-1 gap-8 lg:grid-cols-2 [&>*]:min-w-0" data-testid="studio-settings-panel">
      <div className="flex flex-col gap-4">
        <span className="eyebrow">Station identity</span>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full border-2 border-[color:var(--gold)] overflow-hidden bg-black/40 flex items-center justify-center">
            {logo.has_logo ? <img src={`${API}/station/logo?v=${logo.logo_version}`} alt="Logo" className="w-full h-full object-cover" data-testid="studio-logo-preview" /> : <Disc3 className="w-8 h-8 text-[color:var(--gold)]" />}
          </div>
          <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadImage("logo", e.target.files[0])} data-testid="studio-logo-input" />
          <button onClick={() => file.current.click()} className="rounded-lg border border-white/15 px-4 py-2 text-sm flex items-center gap-2 hover:bg-white/10" data-testid="studio-logo-upload-button"><ImageUp className="w-4 h-4" /> Upload logo</button>
        </div>
        <div className="flex flex-col gap-2">
          <Label>Public player background</Label>
          <div className="relative h-28 rounded-xl overflow-hidden border border-white/10 bg-black flex items-center justify-center" data-testid="studio-background-preview">
            {logo.has_background
              ? <img src={`${API}/station/background?v=${logo.background_version}`} alt="Background" className="w-full h-full object-cover" />
              : <span className="font-mono text-xs text-[color:var(--lr-muted)]">Plain black</span>}
          </div>
          <div className="flex gap-2">
            <input ref={bgFile} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { uploadImage("background", e.target.files[0]); e.target.value = ""; }} data-testid="studio-background-input" />
            <button onClick={() => bgFile.current.click()} className="rounded-lg border border-white/15 px-4 py-2 text-sm flex items-center gap-2 hover:bg-white/10" data-testid="studio-background-upload-button"><ImageUp className="w-4 h-4" /> Upload background</button>
            {logo.has_background && <button onClick={removeBackground} className="rounded-lg border border-white/15 px-4 py-2 text-sm flex items-center gap-2 hover:bg-white/10 text-[color:var(--red)]" data-testid="studio-background-remove-button"><Trash2 className="w-4 h-4" /> Remove</button>}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Station name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="studio-settings-name-input" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Tagline</Label>
          <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="studio-settings-tagline-input" />
        </div>
        <button onClick={save} disabled={saving} className="self-start rounded-lg bg-[color:var(--red)] px-5 py-2.5 font-bold disabled:opacity-60" data-testid="studio-settings-save-button">{saving ? "Saving..." : "Save settings"}</button>
      </div>
      <div className="flex flex-col gap-4">
        <span className="eyebrow">Share the signal</span>
        <CopyRow label="Stream URL" value={STREAM_URL} testid="studio-copy-stream-url" />
        <CopyRow label="HLS stream (.m3u8)" value={`${API}/hls/live.m3u8`} testid="studio-copy-hls-url" />
        <CopyRow label="Playlist (.m3u)" value={`${API}/stream.m3u`} testid="studio-copy-m3u-url" />
        <CopyRow label="Listener page" value={SITE} testid="studio-copy-site-url" />
        <CopyRow label="Embed code" value={EMBED} testid="studio-copy-embed-code" />
      </div>
    </section>
  );
};
